/**
 * "New since last visit" for saved searches.
 *
 * The API has no per-user, per-search visit tracking, so we remember locally
 * (per user) when each saved search was last opened. The management page then
 * asks the live search API how many matching homes were listed since that
 * moment (via `dom_max` = days since last visit) — a real count, never faked.
 *
 * Baseline when a search has never been opened on this device = when it was
 * saved (so the first badge reads "new since you saved this search").
 */
const DAY_MS = 86_400_000;

const keyFor = (userId: string) => `goreal:ss-visits:${userId}`;

type Visits = Record<string, number>; // searchId → epoch ms

function read(userId: string): Visits {
  try {
    const raw = localStorage.getItem(keyFor(userId));
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    return parsed && typeof parsed === 'object' ? (parsed as Visits) : {};
  } catch {
    return {};
  }
}

/** Epoch-ms of the last visit to a saved search, or null if never visited. */
export function getSearchVisit(userId: string, searchId: string): number | null {
  const v = read(userId)[searchId];
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

/** Record "just opened" for a saved search. */
export function recordSearchVisit(userId: string, searchId: string): void {
  try {
    const visits = read(userId);
    visits[searchId] = Date.now();
    localStorage.setItem(keyFor(userId), JSON.stringify(visits));
  } catch {
    // storage unavailable — the badge just won't reset; no crash.
  }
}

/**
 * Whole days since `baselineMs`, clamped to [1, 3650] so it's a valid
 * `dom_max` (the API rejects <1 and caps at ~10 years). Returns at least 1 so
 * a just-saved search still asks a sane window.
 */
export function daysSince(baselineMs: number, now = Date.now()): number {
  const days = Math.ceil((now - baselineMs) / DAY_MS);
  return Math.min(3650, Math.max(1, days));
}
